import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, RefreshCw, Search, Filter, Check, X, MessageSquare,
  AlertTriangle, TrendingUp, TrendingDown, MapPin, Clock, ChevronDown
} from 'lucide-react';
import BackButton from '../components/BackButton';
import { SUDAN_CROPS, getUnitLabel } from '../config/sudanCrops';
import { getFarmerCopy } from '../i18n/farmerMarketCopy';
import { formatDeviation, getDeviationSeverity } from '../utils/priceCheck';
import './AdminPendingAds.css';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

function AdminPendingAds() {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || isArabic;
  const lang = isArabic ? 'ar' : 'en';
  const copy = getFarmerCopy(lang).admin;

  // State
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Filters
  const [cropFilter, setCropFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [highDeviationOnly, setHighDeviationOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch pending ads
  const fetchPendingAds = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cropFilter) params.set('cropSlug', cropFilter);
      if (stateFilter) params.set('state', stateFilter);
      if (searchQuery) params.set('q', searchQuery);
      if (highDeviationOnly) params.set('high', '1');

      const res = await fetch(`${BASE_URL}/api/admin/ads/pending?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || []);
        setTotal(data.total || 0);
      } else {
        throw new Error(`Failed: ${res.status}`);
      }
    } catch (error) {
      console.error('Failed to fetch pending ads:', error);
      toast.error(copy.toast.failed);
    } finally {
      setLoading(false);
    }
  }, [token, cropFilter, stateFilter, searchQuery, highDeviationOnly, copy.toast.failed]);

  useEffect(() => {
    fetchPendingAds();
  }, [fetchPendingAds]);

  // Handle admin decision
  const handleDecision = async (adId, action, note = '') => {
    if (!token) return;
    
    setActionLoading(adId);
    try {
      const res = await fetch(`${BASE_URL}/api/admin/ads/${adId}/decision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ action, note }),
      });

      if (res.ok) {
        toast.success(copy.toast.updated);
        // Remove the item from the list
        setItems(prev => prev.filter(item => item.ad_id !== adId));
        setTotal(prev => Math.max(0, prev - 1));
      } else {
        throw new Error(`Failed: ${res.status}`);
      }
    } catch (error) {
      console.error('Decision failed:', error);
      toast.error(copy.toast.failed);
    } finally {
      setActionLoading(null);
    }
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat(isArabic ? 'ar-SD' : 'en-SD').format(price || 0);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString(isArabic ? 'ar-SD' : 'en-SD', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Check admin access
  if (!user?.is_admin) {
    return (
      <div className="admin-pending-page">
        <div className="access-denied">
          <AlertTriangle size={48} />
          <h2>{isArabic ? 'غير مصرح' : 'Access Denied'}</h2>
          <p>{isArabic ? 'هذه الصفحة للمسؤولين فقط' : 'This page is for admins only'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-pending-page" data-testid="admin-pending-ads">
      {/* Header */}
      <div className="pending-header">
        <BackButton className="back-btn" testId="pending-ads-back" size={20} />
        <h1 className="page-title" data-testid="pending-ads-title">
          {/* iter309 — Title shows the count for the active filter so
              operators see at a glance how many pending ads await
              their review. Matches Admin Overview "Pending" pill. */}
          {copy.pendingTitle} ({total})
        </h1>
        <button 
          className="refresh-btn"
          onClick={fetchPendingAds}
          disabled={loading}
        >
          <RefreshCw size={20} className={loading ? 'spinning' : ''} />
        </button>
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-value">{total}</span>
          <span className="stat-label">{isArabic ? 'قيد المراجعة' : 'Pending'}</span>
        </div>
        <button 
          className={`filter-toggle ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={18} />
          <span>{copy.filters.all}</span>
          <ChevronDown size={16} className={showFilters ? 'rotated' : ''} />
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          {/* Search */}
          <div className="filter-row">
            <div className="search-input-wrapper">
              <Search size={16} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={copy.filters.search}
                className="filter-input"
              />
            </div>
          </div>

          {/* Crop & State */}
          <div className="filter-row">
            <select
              value={cropFilter}
              onChange={(e) => setCropFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">{copy.filters.all} - {copy.filters.crop}</option>
              {SUDAN_CROPS.filter(c => c.slug !== 'farm_services').map(crop => (
                <option key={crop.slug} value={crop.slug}>
                  {lang === 'ar' ? crop.nameAr : crop.nameEn}
                </option>
              ))}
            </select>

            <input
              type="text"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              placeholder={copy.filters.state}
              className="filter-input"
            />
          </div>

          {/* High Deviation Toggle */}
          <div className="filter-row">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={highDeviationOnly}
                onChange={(e) => setHighDeviationOnly(e.target.checked)}
              />
              <span>{copy.filters.highDeviation}</span>
            </label>
          </div>
        </div>
      )}

      {/* Pending Ads List */}
      <div className="pending-list">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{isArabic ? 'جارٍ التحميل...' : 'Loading...'}</p>
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <Check size={48} className="empty-icon" />
            <h3>{copy.noPending}</h3>
            <p>{isArabic ? 'جميع الإعلانات تمت مراجعتها' : 'All ads have been reviewed'}</p>
          </div>
        ) : (
          items.map((ad) => {
            const deviation = ad.price_check?.deviation_pct;
            const marketAvg = ad.price_check?.market_avg_at_post;
            const severity = getDeviationSeverity(deviation);
            const isProcessing = actionLoading === ad.ad_id;

            return (
              <div key={ad.ad_id} className={`pending-card ${severity}`} data-testid={`pending-ad-${ad.ad_id}`}>
                {/* Ad Info */}
                <div className="ad-info">
                  <h3 className="ad-title">{ad.title}</h3>
                  <div className="ad-meta">
                    {ad.crop_slug && (
                      <span className="meta-chip crop">
                        {SUDAN_CROPS.find(c => c.slug === ad.crop_slug)?.icon} 
                        {lang === 'ar' 
                          ? SUDAN_CROPS.find(c => c.slug === ad.crop_slug)?.nameAr 
                          : SUDAN_CROPS.find(c => c.slug === ad.crop_slug)?.nameEn}
                      </span>
                    )}
                    {(ad.state || ad.city) && (
                      <span className="meta-chip location">
                        <MapPin size={12} />
                        {ad.state}{ad.city ? ` • ${ad.city}` : ''}
                      </span>
                    )}
                    <span className="meta-chip time">
                      <Clock size={12} />
                      {formatDate(ad.created_at)}
                    </span>
                  </div>
                </div>

                {/* Price Comparison */}
                <div className="price-comparison">
                  <div className="price-item asking">
                    <span className="price-label">{copy.columns.asking}</span>
                    <span className="price-value">
                      {formatPrice(ad.price)} SDG
                      {ad.unit && <small>/{getUnitLabel(ad.unit, lang)}</small>}
                    </span>
                  </div>
                  
                  <div className="price-item market">
                    <span className="price-label">{copy.columns.marketAvg}</span>
                    <span className="price-value">
                      {marketAvg ? `${formatPrice(marketAvg)} SDG` : '--'}
                    </span>
                  </div>
                  
                  <div className={`price-item deviation ${severity}`}>
                    <span className="price-label">{copy.columns.deviation}</span>
                    <span className="price-value">
                      {deviation !== null && deviation !== undefined ? (
                        <>
                          {deviation > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                          {formatDeviation(deviation)}
                        </>
                      ) : '--'}
                    </span>
                  </div>
                </div>

                {/* Flag Reason */}
                {ad.price_check?.flag_reason && (
                  <div className="flag-reason">
                    <AlertTriangle size={14} />
                    <span>{ad.price_check.flag_reason}</span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="action-buttons">
                  <button
                    className="action-btn approve"
                    onClick={() => handleDecision(ad.ad_id, 'APPROVE', copy.notes.approveNote)}
                    disabled={isProcessing}
                    data-testid={`approve-${ad.ad_id}`}
                  >
                    <Check size={16} />
                    <span>{copy.decisions.approve}</span>
                  </button>
                  
                  <button
                    className="action-btn request"
                    onClick={() => handleDecision(ad.ad_id, 'REQUEST_CHANGE', copy.notes.requestNote)}
                    disabled={isProcessing}
                    data-testid={`request-${ad.ad_id}`}
                  >
                    <MessageSquare size={16} />
                    <span>{copy.decisions.requestChange}</span>
                  </button>
                  
                  <button
                    className="action-btn reject"
                    onClick={() => handleDecision(ad.ad_id, 'REJECT', copy.notes.rejectNote)}
                    disabled={isProcessing}
                    data-testid={`reject-${ad.ad_id}`}
                  >
                    <X size={16} />
                    <span>{copy.decisions.reject}</span>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default AdminPendingAds;
